import { Field, useForm, useFormState } from 'react-final-form';
import { isEmpty } from '@rainbow-modules/validation';
import CalendarAvailabilityInput from 'components/CalendarAvailabilityInput';
import CalendarHolidayInput from 'components/CalendarHolidayInput';
import HolidayImportButton from 'components/CalendarHolidayInput/importButton';
import { CalendarHoliday } from 'components/CalendarHolidayInput/parseIcsHolidays';
import CalendarEventStatusSelect from 'components/CalendarEventStatusSelect';
import CalendarEventStatusPicker from 'components/CalendarEventStatusPicker';
import CalendarEventTypeSelect from 'components/CalendarEventTypeSelect';
import ColorPicklist from 'components/ColorPicklist';
import {
    FormSection,
    FormSectionHeader,
    SectionTitle,
    SectionTitleRow,
    SectionDescription,
    FieldsContainer,
    StyledInput,
    StyledOverlapToggle,
} from './styled';

const validateNotEmpty = (value: unknown) => {
    if (isEmpty(value)) return 'This field is required';
    return undefined;
};

const validateHolidays = (holidays: CalendarHoliday[] = []) => {
    const dates = new Set<string>();
    const invalidHoliday = holidays.find((holiday) => {
        const date = new Date(Date.UTC(2000, holiday.month - 1, holiday.day));
        const dateKey = `${holiday.month}-${holiday.day}`;
        const isDuplicate = dates.has(dateKey);
        dates.add(dateKey);
        return !holiday.name?.trim()
            || date.getUTCMonth() !== holiday.month - 1
            || date.getUTCDate() !== holiday.day
            || isDuplicate;
    });
    if (!invalidHoliday) return undefined;
    const dateKey = `${invalidHoliday.month}-${invalidHoliday.day}`;
    const isDuplicate = holidays.filter(
        (holiday) => `${holiday.month}-${holiday.day}` === dateKey,
    ).length > 1;
    return isDuplicate
        ? 'A holiday date is duplicated.'
        : 'Enter a name and valid date for every holiday.';
};

interface Params {
    agentId?: string;
}

const FormFields = ({
    agentId = '',
}: Params) => {
    const { values } = useFormState();
    const { change } = useForm();

    return (
        <>
            <FormSection>
                <FormSectionHeader>
                    <SectionTitle>Calendar Styles</SectionTitle>
                </FormSectionHeader>
                <FieldsContainer>
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={StyledInput}
                        name="name"
                        label="Calendar Name"
                        labelAlignment="left"
                        placeholder="Enter a name for your calendar"
                        borderRadius="semi-square"
                        autoComplete="off"
                        required
                        validate={validateNotEmpty}
                    />
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={ColorPicklist}
                        name="color"
                        label="Color"
                        labelAlignment="left"
                        placeholder="Select Color"
                        borderRadius="semi-square"
                        required
                        validate={validateNotEmpty}
                    />
                </FieldsContainer>
            </FormSection>
            <FormSection>
                <FormSectionHeader>
                    <SectionTitle>Scheduling</SectionTitle>
                    <SectionDescription>
                        Control whether this calendar can accept simultaneous events.
                    </SectionDescription>
                </FormSectionHeader>
                <FieldsContainer>
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={StyledOverlapToggle}
                        name="allowOverlappingEvents"
                        label="Allow overlapping events"
                        labelAlignment="left"
                    />
                </FieldsContainer>
            </FormSection>
            <FormSection>
                <FormSectionHeader>
                    <SectionTitle>Event Type</SectionTitle>
                    <SectionDescription>Add description here</SectionDescription>
                </FormSectionHeader>
                <FieldsContainer>
                    <Field
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                        component={CalendarEventTypeSelect}
                        agentId={agentId}
                        name="eventTypes"
                        required
                        validate={validateNotEmpty}
                    />
                </FieldsContainer>
            </FormSection>
            <FormSection>
                <FormSectionHeader>
                    <SectionTitle>Event Status</SectionTitle>
                    <SectionDescription>Add description here</SectionDescription>
                </FormSectionHeader>
                <FieldsContainer>
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={CalendarEventStatusSelect}
                        agentId={agentId}
                        name="eventStatuses"
                        required
                        validate={validateNotEmpty}
                    />
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={CalendarEventStatusPicker}
                        name="defaultEventStatusId"
                        label="Default Status"
                        labelAlignment="left"
                        placeholder="Select the default status for new events"
                        borderRadius="semi-square"
                        required
                        eventStatuses={values?.eventStatuses}
                        validate={validateNotEmpty}
                    />
                </FieldsContainer>
            </FormSection>
            <FormSection>
                <FormSectionHeader>
                    <SectionTitle>Calendar Availability</SectionTitle>
                    <SectionDescription>
                        Set when you’re regularly available for events.
                    </SectionDescription>
                </FormSectionHeader>
                <FieldsContainer>
                    <Field
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                        component={CalendarAvailabilityInput}
                        name="availability"
                        required
                    />
                </FieldsContainer>
            </FormSection>
            <FormSection>
                <FormSectionHeader>
                    <SectionTitleRow>
                        <SectionTitle>Holidays</SectionTitle>
                        <HolidayImportButton
                            value={values?.holidays}
                            onChange={(holidays) => change('holidays', holidays)}
                        />
                    </SectionTitleRow>
                    <SectionDescription>
                        Add named dates that repeat every year, or import them from an ICS file.
                    </SectionDescription>
                </FormSectionHeader>
                <FieldsContainer>
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={CalendarHolidayInput}
                        name="holidays"
                        validate={validateHolidays}
                    />
                </FieldsContainer>
            </FormSection>
        </>
    );
};

export default FormFields;
